/**
 * What an upload pane accepts, in one place.
 *
 * The same definition drives three things that have to agree: the `accept` attribute, so
 * the file picker greys out everything else; the filter on dropped files, because a drop
 * ignores `accept` entirely; and the line under the drop target that says what to send.
 * Mirrors `BusinessOnboardings::InvoiceSampleUploadService` on the API, which is what
 * actually enforces it.
 */
export interface UploadFileTypes {
  /** Named under the drop target, in the customer's words. */
  label: string;
  extensions: readonly string[];
  mimeTypes: readonly string[];
  /** One invoice. */
  fileMaxBytes: number;
  /**
   * An archive holds many invoices, so it cannot be held to one invoice's size. A
   * hundred PDFs is routinely tens of megabytes, and the per-file ceiling would refuse
   * the very thing we asked for.
   */
  archiveExtensions: readonly string[];
  archiveMaxBytes: number;
}

const MEGABYTE = 1024 * 1024;

export const INVOICE_UPLOAD_TYPES: UploadFileTypes = {
  label: "PDFs, photos, or a zip of them",
  extensions: [".pdf", ".png", ".jpg", ".jpeg", ".heic", ".zip"],
  mimeTypes: [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/heic",
    "application/zip",
    // What Windows sends for a zip, which is not the registered type.
    "application/x-zip-compressed",
  ],
  fileMaxBytes: 20 * MEGABYTE,
  archiveExtensions: [".zip"],
  archiveMaxBytes: 100 * MEGABYTE,
};

/** The `accept` attribute. Extensions carry it on macOS, mime types on Windows. */
export const acceptAttribute = (types: UploadFileTypes) =>
  [...types.extensions, ...types.mimeTypes].join(",");

/** The extension decides. A browser's content type for a zip or a HEIC is unreliable. */
export const isAcceptedFile = (file: File, types: UploadFileTypes) =>
  types.extensions.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

/** Mirrors MAX_FILE_BYTES and MAX_ARCHIVE_BYTES on the API. */
export const sizeLimitFor = (file: File, types: UploadFileTypes) =>
  types.archiveExtensions.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  )
    ? types.archiveMaxBytes
    : types.fileMaxBytes;
